import React from "react";
import { observer } from "mobx-react";
import { PhonogramUpdateStore } from "../../store";
import { PhonogramUpdateForm } from "./PhonogramUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";

export type PhonogramUpdateProps = {
  phonogramUpdateStore: PhonogramUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const PhonogramUpdate: React.FC<PhonogramUpdateProps> = observer(
  ({ phonogramUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={phonogramUpdateStore.isSaving} />
        <PhonogramUpdateForm
          phonogramUpdateStore={phonogramUpdateStore}
          trackNameError={phonogramUpdateStore.errors.trackName?.message}
          onTrackNameChange={(value: string) => {
            phonogramUpdateStore.setTrackNameValue(value);
          }}
          trackName={phonogramUpdateStore.entityValues.trackName}
          performersError={phonogramUpdateStore.errors.performers?.message}
          onPerformersChange={(value) => {
            phonogramUpdateStore.setPerformersValue(value);
          }}
          performersSelectedValues={
            phonogramUpdateStore.entityValues.showPerformers
          }
          onPerformersSelected={(value) => {
            phonogramUpdateStore.setSelectedPerformers(value);
          }}
          performersOptions={phonogramUpdateStore.getPerformersOptions()}
          albumNameError={phonogramUpdateStore.errors.albumName?.message}
          onAlbumNameChange={(value: string) => {
            phonogramUpdateStore.setAlbumNameValue(value);
          }}
          albumName={phonogramUpdateStore.entityValues.albumName}
          publicationYearError={
            phonogramUpdateStore.errors.publicationYear?.message
          }
          onPublicationYearChange={(value: number | undefined) => {
            phonogramUpdateStore.setPublicationYearValue(value);
          }}
          publicationYear={phonogramUpdateStore.entityValues.publicationYear}
          isrcError={phonogramUpdateStore.errors.isrc?.message}
          onIsrcChange={(value: string) => {
            phonogramUpdateStore.setIsrcValue(value);
          }}
          isrc={phonogramUpdateStore.entityValues.isrc}
          originalProducerError={
            phonogramUpdateStore.errors.originalProducer?.message
          }
          onOriginalProducerChange={(value: string) => {
            phonogramUpdateStore.setOriginalProducerValue(value);
          }}
          originalProducer={phonogramUpdateStore.entityValues.originalProducer}
          audioFileOnRestoreFile={(id: string) =>
            phonogramUpdateStore.audioFileRestoreFile(id)
          }
          audioFileOnLoadFile={(file: any) =>
            phonogramUpdateStore.audioFileUploadFile(file)
          }
          audioFileOnCommitFile={(changes) =>
            phonogramUpdateStore.audioFileCommitFile(changes)
          }
          audioFileFiles={phonogramUpdateStore.entityValues.__audioFile_files}
          audioFileError={phonogramUpdateStore.errors.audioFile?.message}
          mainAudioError={phonogramUpdateStore.errors.mainAudio?.message}
          onMainAudioChange={(value: boolean) => {
            phonogramUpdateStore.setMainAudioValue(value);
          }}
          mainAudio={phonogramUpdateStore.entityValues.mainAudio}
          musicalWorkLinkUpdateStore={phonogramUpdateStore.linksUpdateStore}
          linksVisibleColumns={phonogramUpdateStore.linksVisibleColumns}
          linksColumnTypes={phonogramUpdateStore.linksColumnTypes}
          linksOnRestore={(id: string) => phonogramUpdateStore.linksRestore(id)}
          linksOnCommitChanges={(changes) =>
            phonogramUpdateStore.linksCommitChanges(changes)
          }
          linksOnCommitInlineChange={(rowId, changes) =>
            phonogramUpdateStore.linksCommitInlineChange(rowId, changes)
          }
          linksValidateChanges={(changes) =>
            phonogramUpdateStore.linksValidateChanges(changes)
          }
          linksRows={phonogramUpdateStore.entityValues.showLinks}
          linksError={phonogramUpdateStore.errors.links?.message}
          notesError={phonogramUpdateStore.errors.notes?.message}
          onNotesChange={(value: string) => {
            phonogramUpdateStore.setNotesValue(value);
          }}
          notes={phonogramUpdateStore.entityValues.notes}
          dummyCRUDRestoredObservable={
            phonogramUpdateStore.dummyCRUDRestoredObservable
          }
          recordId={phonogramUpdateStore.phonogramInstance?.id || ""}
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={phonogramUpdateStore.saveDisabled}
          onSave={() => phonogramUpdateStore.savePhonogram()}
          onCancel={() => phonogramUpdateStore.cancelPhonogram()}
        />
        <ErrorDialog
          open={phonogramUpdateStore.errorDialogOpened}
          title={phonogramUpdateStore.errorTitle}
          description={phonogramUpdateStore.errorDescription}
          onClose={() => phonogramUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={phonogramUpdateStore.confirmDialogOpened}
          title={phonogramUpdateStore.confirmDialogTitle}
          description={phonogramUpdateStore.confirmDialogDescription}
          onConfirm={() => phonogramUpdateStore.executeConfirmedAction()}
          onCancel={() => phonogramUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
